// Race history kept in this browser: every live race watched to the end, and statistics over them.
// Recorded races are left out: the fallback recording is one race played on repeat.
import { CAT_IDS, type CatId, type RaceEvent, type RaceSnapshot } from '../race/types';
import { hasPhotoFinish } from './photoFinish';

export interface RaceRecord {
  /** Wall time the official results arrived, ms since epoch. Also the record's id. */
  at: number;
  /** Wall time the race was first seen running (the page load time if joined mid-race). Missing in older records. */
  startedAt?: number;
  /** Joined after the start: ham, catnip and lead counts miss the part before. */
  joinedMidRace: boolean;
  results: { id: CatId; place: number; seconds: number; dsq: boolean }[];
  /** Cats that ate the ham, in the order they started eating. */
  ham: CatId[];
  catnip: { cat: CatId; delta: number | null }[];
  leadChanges: number;
  /** The official times put two cats under half a second apart, so the finish camera was shown. */
  photoFinish: boolean;
}

export interface CatStats {
  id: CatId;
  races: number;
  wins: number;
  podiums: number;
  dsq: number;
  /** Mean place over races not disqualified, or null. */
  avgPlace: number | null;
  bestSeconds: number | null;
  hams: number;
  catnips: number;
}

export interface HistoryStats {
  races: number;
  cats: CatStats[]; // lane order
  fastestWin: { id: CatId; seconds: number; at: number } | null;
  photoFinishes: number;
  first: number | null;
  last: number | null;
}

const STORAGE_KEY = 'kd-history';
/** Oldest races drop off past this. About a week of watching around the clock. */
export const MAX_RECORDS = 2000;

interface Draft { raceNo: number; startedAt: number; joinedMidRace: boolean; ham: CatId[]; catnip: RaceRecord['catnip']; leadChanges: number }

/** Follows the broadcast and hands over a record when a live race it saw running gets its results. */
export class RaceRecorder {
  private draft: Draft | null = null;

  constructor(private onRecord: (r: RaceRecord) => void, private now: () => number = Date.now) {}

  update(snap: RaceSnapshot, events: RaceEvent[], live: boolean): void {
    if (!live) { this.draft = null; return; }
    for (const e of events) {
      if (e.type === 'phase' && e.phase === 'running') this.draft = this.fresh(e.raceNo, snap.joinedMidRace);
    }
    // Joined a race already under way: still worth recording, flagged.
    if (snap.phase === 'running' && this.draft?.raceNo !== snap.raceNo) this.draft = this.fresh(snap.raceNo, snap.joinedMidRace);
    const d = this.draft;
    if (!d || d.raceNo !== snap.raceNo) return;
    for (const e of events) {
      switch (e.type) {
        case 'ham-start': if (!d.ham.includes(e.cat)) d.ham.push(e.cat); break;
        case 'catnip-catch': d.catnip.push({ cat: e.cat, delta: null }); break;
        case 'catnip-landing': {
          const open = [...d.catnip].reverse().find((c) => c.cat === e.cat && c.delta === null);
          if (open) open.delta = e.delta; else d.catnip.push({ cat: e.cat, delta: e.delta });
          break;
        }
        case 'lead-change': d.leadChanges++; break;
        case 'results':
          this.onRecord({
            at: this.now(), startedAt: d.startedAt, joinedMidRace: d.joinedMidRace,
            results: e.results.map((r) => ({ id: r.id, place: r.place, seconds: r.seconds, dsq: r.dsq })),
            ham: d.ham, catnip: d.catnip, leadChanges: d.leadChanges, photoFinish: hasPhotoFinish(e.results),
          });
          this.draft = null;
          return;
      }
    }
  }

  private fresh(raceNo: number, joinedMidRace: boolean): Draft {
    return { raceNo, startedAt: this.now(), joinedMidRace, ham: [], catnip: [], leadChanges: 0 };
  }
}

type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem' | 'removeItem'>;
const defaultStorage = (): Storage | undefined => { try { return globalThis.localStorage; } catch { return undefined; } };

function isRecord(r: unknown): r is RaceRecord {
  const x = r as RaceRecord;
  return !!x && typeof x.at === 'number' && Array.isArray(x.results) && Array.isArray(x.ham) && Array.isArray(x.catnip);
}

export function loadHistory(storage: Storage | undefined = defaultStorage()): RaceRecord[] {
  try {
    const raw = JSON.parse(storage?.getItem(STORAGE_KEY) ?? '[]') as unknown;
    return Array.isArray(raw) ? raw.filter(isRecord) : [];
  } catch { return []; }
}

/** Adds a race, oldest first, and saves. Skips a duplicate of the last race (same results within 10 minutes). */
export function appendHistory(list: RaceRecord[], r: RaceRecord, storage: Storage | undefined = defaultStorage()): RaceRecord[] {
  const last = list[list.length - 1];
  const same = (a: RaceRecord, b: RaceRecord) => JSON.stringify(a.results) === JSON.stringify(b.results);
  if (last && r.at - last.at < 10 * 60_000 && same(last, r)) return list;
  const next = [...list, r].slice(-MAX_RECORDS);
  try { storage?.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* storage full or blocked: keep it for this visit */ }
  return next;
}

export function clearHistory(storage: Storage | undefined = defaultStorage()): RaceRecord[] {
  try { storage?.removeItem(STORAGE_KEY); } catch { /* storage blocked */ }
  return [];
}

export function computeStats(list: readonly RaceRecord[]): HistoryStats {
  const cats = CAT_IDS.map((id): CatStats & { placeSum: number; placed: number } => ({
    id, races: 0, wins: 0, podiums: 0, dsq: 0, avgPlace: null, bestSeconds: null, hams: 0, catnips: 0, placeSum: 0, placed: 0,
  }));
  let fastestWin: HistoryStats['fastestWin'] = null;
  let photoFinishes = 0;
  for (const r of list) {
    if (r.photoFinish) photoFinishes++;
    for (const res of r.results) {
      const c = cats[CAT_IDS.indexOf(res.id)];
      if (!c) continue;
      c.races++;
      if (res.dsq) { c.dsq++; continue; }
      c.placeSum += res.place; c.placed++;
      if (res.place === 1) c.wins++;
      if (res.place <= 3) c.podiums++;
      if (c.bestSeconds === null || res.seconds < c.bestSeconds) c.bestSeconds = res.seconds;
      if (res.place === 1 && (!fastestWin || res.seconds < fastestWin.seconds)) fastestWin = { id: res.id, seconds: res.seconds, at: r.at };
    }
    for (const id of r.ham) { const c = cats[CAT_IDS.indexOf(id)]; if (c) c.hams++; }
    for (const n of r.catnip) { const c = cats[CAT_IDS.indexOf(n.cat)]; if (c) c.catnips++; }
  }
  return {
    races: list.length,
    cats: cats.map(({ placeSum, placed, ...c }) => ({ ...c, avgPlace: placed ? placeSum / placed : null })),
    fastestWin,
    photoFinishes,
    first: list[0]?.at ?? null,
    last: list[list.length - 1]?.at ?? null,
  };
}
